/**
 * Column-based tree builder for Materials, Rentals, Subcontractors, Other pages.
 *
 * Instead of parsing Forecast_Row_ID path strings, this builds the tree
 * by reading individual column values (Phase_Code, Supplier_Name, PO_Number, etc.)
 * from each data row. This eliminates:
 *   - duplication from mismatched path segments vs actual column values
 *   - double-counted totals on parent nodes
 *   - inflexibility when pivoting by different groupings
 *
 * Usage:
 *   const tree = buildColumnTree(rows, drillPath, userEtcMap, expandedMap, phaseDayZero)
 *   // drillPath e.g. ['Phase_Code', 'Supplier_Name', 'PO_Number', 'Line_ID']
 */

import { pNum } from '../db/company';

// ── helpers ────────────────────────────────────────────────────────────
const norm = v => {
    if (v === null || v === undefined) return '';
    const s = String(v).trim();
    return (s === 'undefined' || s === 'null') ? '' : s;
};

/**
 * Derive a composite key for a row at a given depth in the drill path.
 * The key = column values joined by ^, e.g. "^140403^Acme^22100"
 */
const breadcrumbAt = (row, drillPath, depth) => {
    let bc = '';
    for (let i = 0; i <= depth; i++) {
        bc += '^' + norm(row[drillPath[i]]);
    }
    return bc;
};

/**
 * For user-added rows (Forecast_Row_ID contains ^~u), figure out which
 * drill depth they belong to by counting segments.
 */
const userRowDepth = (rowId, isPhased) => {
    if (!rowId || !rowId.includes('^~u')) return -1;
    // segments: e.g. "^140403^vendor^~u.abc" => ['','140403','vendor','~u.abc']
    const segs = rowId.split('^').filter(s => s !== '');
    // The ~u segment itself is NOT a tree level; the level = index of ~u - 1
    const uIdx = segs.findIndex(s => s.startsWith('~u'));
    return uIdx >= 0 ? uIdx : -1;
};

// ── main builder ───────────────────────────────────────────────────────

/**
 * Build the tree from flat data rows using column values only.
 *
 * @param {Array}  rows         – combined array of open commitments, cost history, and user ETC rows
 * @param {Array}  drillPath    – ordered column names defining the hierarchy, e.g. ['Phase_Code','Supplier_Name','PO_Number','Line_ID']
 * @param {Object} userEtcMap   – map of breadcrumb → { etc, costType, notes, description, title, rowId, ... }
 * @param {Object} expandedMap  – map of breadcrumb → boolean (expanded state)
 * @param {Object} phaseDayZero – map of phase code → budget amount (for variance)
 * @param {Object} opts         – { leafPath: 'PO_Detail' } optional; name of the virtual leaf level
 * @returns {Object} tree – keyed by top-level grouping value
 */
export const buildColumnTree = (rows, drillPath, userEtcMap = {}, expandedMap = {}, phaseDayZero = {}, opts = {}) => {
    const tree = opts.seedTree ? JSON.parse(JSON.stringify(opts.seedTree)) : {};
    const leafPath = opts.leafPath || 'PO_Detail';

    // Track which userEtcMap entries were matched so we can report unmatched
    const matchedUserKeys = new Set();

    // Counter for deterministic leaf keys (avoids Math.random which causes React to unmount/remount all leaves on rebuild)
    const leafCounters = {};

    rows.forEach(row => {
        const isUserRow = row.Forecast_Row_ID && row.Forecast_Row_ID.includes('^~u');
        const isUserDataRow = row.Forecast_Row_ID && !row.Forecast_Row_ID.includes('^~u') && !!row.Forecast_Tab;
        const isResourceSeed = !!row._isResourceSeed;

        // For user-added rows, determine how deep they go in the drill path
        const uDepth = isUserRow ? userRowDepth(row.Forecast_Row_ID) : -1;
        // How many levels of the drill path this row should create nodes for
        const maxDepth = isUserRow && uDepth >= 0 ? Math.min(uDepth, drillPath.length) : drillPath.length;

        let currentLevel = tree;
        let breadcrumb = '';

        for (let depth = 0; depth < maxDepth; depth++) {
            const col = drillPath[depth];
            const key = norm(row[col]) || '';
            breadcrumb += '^' + key;

            if (!currentLevel[key]) {
                // Check if user ETC exists at this breadcrumb
                const userEntry = userEtcMap[breadcrumb];
                if (userEntry) {
                    matchedUserKeys.add(breadcrumb);
                    userEntry.found = true;
                }

                currentLevel[key] = {
                    rows: {},
                    data: {
                        detail: row,
                        indent: depth,
                        path: col,
                        key: key,
                        breadcrumb: breadcrumb,
                        etc: userEntry ? userEntry.etc : '',
                        costType: userEntry?.costType || '',
                        notes: userEntry?.notes || '',
                        description: userEntry?.description || '',
                        title: userEntry?.title || '',
                        expanded: expandedMap[breadcrumb] || false,
                    }
                };
            } else {
                // Node exists — update user entry if present
                const userEntry = userEtcMap[breadcrumb];
                if (userEntry) {
                    matchedUserKeys.add(breadcrumb);
                    userEntry.found = true;
                    if (userEntry.etc !== undefined && userEntry.etc !== '') {
                        currentLevel[key].data.etc = userEntry.etc;
                    }
                    if (userEntry.notes) currentLevel[key].data.notes = userEntry.notes;
                    if (userEntry.description) currentLevel[key].data.description = userEntry.description;
                    if (userEntry.title) currentLevel[key].data.title = userEntry.title;
                }
                // If this row is from OC data, update the detail to keep latest OC info
                if (row.OC) {
                    currentLevel[key].data.detail = row;
                }
            }

            // Ensure node has rows object (seedTree nodes may not have it)
            if (!currentLevel[key].rows) {
                currentLevel[key].rows = {};
            }

            // If this is the last level for a user-added row, mark it as a leaf
            if (isUserRow && depth === maxDepth - 1) {
                // User row terminates here — add as leaf under this node
                const rowId = (row.Forecast_Row_ID || '').toLowerCase();
                const userEntry = userEtcMap[breadcrumb];
                if (userEntry) {
                    matchedUserKeys.add(breadcrumb);
                    userEntry.found = true;
                }
                currentLevel[key].rows[rowId] = {
                    rows: false,
                    data: {
                        detail: row,
                        indent: depth + 1,
                        path: col,
                        key: key,
                        breadcrumb: breadcrumb,
                        etc: row.Value_Amount || '',
                        costType: userEntry?.costType || '',
                        notes: userEntry?.notes || '',
                        description: userEntry?.description || '',
                        title: userEntry?.title || '',
                        expanded: false,
                    }
                };
                break;
            }

            // If this is a non-user row saved at exactly this breadcrumb (isUserDataRow),
            // apply ETC at this level and stop recursion
            if (isUserDataRow && row.Forecast_Row_ID === breadcrumb) {
                const userEntry = userEtcMap[breadcrumb];
                if (userEntry) {
                    matchedUserKeys.add(breadcrumb);
                    userEntry.found = true;
                    currentLevel[key].data.etc = userEntry.etc;
                }
                break;
            }

            // Resource seed rows only exist to materialize Resource_Code nodes
            // for budget-only resources; they don't carry data, so don't recurse
            // past the Resource_Code level and don't create empty leaves.
            if (isResourceSeed && col === 'Resource_Code') {
                break;
            }

            // At the last drill level, create leaf entries
            if (depth === maxDepth - 1) {
                // Create a leaf node keyed by a deterministic identifier (stable across rebuilds so React can reconcile)
                const counterPrefix = breadcrumb + (row.OC ? '_oc' : row.CH ? '_ch' : '_leaf');
                leafCounters[counterPrefix] = (leafCounters[counterPrefix] || 0) + 1;
                const leafKey = row.OC
                    ? 'oc_' + (row.PO_Line_Number || '') + '_' + leafCounters[counterPrefix]
                    : row.CH
                    ? 'ch_' + (row.GL_Date || '') + '_' + (row.Remark_Description || '').substring(0, 20) + '_' + leafCounters[counterPrefix]
                    : 'leaf_' + leafCounters[counterPrefix];

                currentLevel[key].rows[leafKey] = {
                    rows: false,
                    data: {
                        detail: row,
                        indent: depth + 1,
                        path: leafPath,
                        key: leafKey,
                        breadcrumb: breadcrumb + '^' + leafKey,
                        etc: '',
                    }
                };
            } else {
                currentLevel = currentLevel[key].rows;
            }
        }
    });

    return tree;
};


// ── totals ─────────────────────────────────────────────────────────────

/**
 * Calculate totals bottom-up for the entire tree. Each parent total is
 * strictly the sum of its children plus any user ETC entered at that specific level.
 * This eliminates double-counting.
 *
 * @param {Object} node         – a single tree node { rows, data }
 * @param {Object|Function} phaseDayZero – phase → budget map for variance calculation, or a function(node) → budget
 * @returns {Object} the same node, with actualTotal / openTotal / etcTotal / totalTotal / maxGLDate set
 */
export const calculateColumnTotals = (node, phaseDayZero) => {
    if (!node || !node.data) return node;

    const hasEtc = v => v !== undefined && v !== null && `${v}` !== '';
    const budgetFn = typeof phaseDayZero === 'function' ? phaseDayZero : null;

    if (node.rows && node.rows !== false && Object.keys(node.rows).length > 0) {
        // ── Internal node: sum children ──
        let actualTotal = 0;
        let openTotal = 0;
        let etcTotal = 0;
        let maxGLDate = '';

        Object.values(node.rows).forEach(child => {
            calculateColumnTotals(child, phaseDayZero);

            if (child.data) {
                actualTotal += pNum(child.data.actualTotal);
                openTotal += pNum(child.data.openTotal);
                etcTotal += pNum(child.data.etcTotal);

                if (child.data.maxGLDate && child.data.maxGLDate > maxGLDate) {
                    maxGLDate = child.data.maxGLDate;
                }
            }
        });

        // Add THIS node's own directly-entered ETC (not children's)
        const parentEtc = hasEtc(node.data.etc) ? pNum(node.data.etc) : 0;
        etcTotal += parentEtc;

        const totalTotal = actualTotal + openTotal + etcTotal;

        node.data.actualTotal = +actualTotal.toFixed(2);
        node.data.openTotal = +openTotal.toFixed(2);
        node.data.etcTotal = +etcTotal.toFixed(2);
        node.data.totalTotal = +totalTotal.toFixed(2);
        node.data.maxGLDate = maxGLDate;

        // Variance to budget (only at Phase or CostType level where budget exists)
        const currentBudget = budgetFn
            ? budgetFn(node)
            : (node.data.path === 'Cost_Type_Code' && phaseDayZero
                ? (phaseDayZero[node.data.key] ?? 0)
                : node.data.path === 'Phase_Code' && phaseDayZero
                ? (phaseDayZero[node.data.key] ?? '')
                : '');
        node.data.resolvedBudget = currentBudget;
        node.data.varianceBudget = currentBudget !== '' ? totalTotal - pNum(currentBudget) : '';

        return node;
    } else {
        // ── Leaf node: read directly from detail ──
        const actualCosts = pNum(node.data.detail?.Job_Cost);
        const open = pNum(node.data.detail?.PO_True_Open_Amount);
        // Only use node.data.etc (explicitly set during tree building).
        // Never fall through to detail.etc — that would double-count user ETC
        // which is already applied at the parent node via userEtcMap.
        const etc = hasEtc(node.data.etc) ? pNum(node.data.etc) : 0;
        const total = actualCosts + open + etc;
        const maxGLDate = node.data.detail?.GL_Date || '';

        node.data.actualTotal = actualCosts;
        node.data.openTotal = open;
        node.data.etcTotal = etc;
        node.data.totalTotal = total;
        node.data.maxGLDate = maxGLDate;

        return node;
    }
};
